import { KYCStatus } from "@/app/core/entities/business.entity";
import { ELoadingStatus } from "@/app/core/models/loading-status.model";
import { resolveRoutedKycStatus } from "@/app/core/utils/kyc.util";
import { loadCurrentUser } from "@/app/stores/auth/auth.actions";
import { selectorBusinessDetail, selectorCurrentUser, selectorCurrentUserStatus } from "@/app/stores/auth/auth.selectors";
import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { combineLatest, of } from "rxjs";
import { filter, switchMap, take, map } from "rxjs/operators";

export const kycGuard: CanActivateFn = (route, state) => {
  const store = inject(Store);
  const router = inject(Router);

  const businessId =
    route.params["businessId"] ||
    route.parent?.params["businessId"] ||
    route.parent?.parent?.params["businessId"];

  const urlTree = router.parseUrl(state.url);
  const segments = urlTree.root.children["primary"]?.segments.map((s) => s.path) ?? [];

  // Find the index of the businessId in the primary URL segments, then look at the next segment
  const businessIdIndex = businessId ? segments.indexOf(String(businessId)) : -1;
  const nextSegment = businessIdIndex >= 0 ? segments[businessIdIndex + 1] : undefined;

  // Routes accessible regardless of KYC status
  const isProfileRoute = nextSegment === "profile";
  const isDashboardRoute = nextSegment === "dashboard";
  const isVerifyBusinessRoute = nextSegment === "verify-business";

  // Ensure user is loaded before deciding, but only trigger the load once per app boot
  return store.select(selectorCurrentUserStatus).pipe(
    take(1),
    switchMap((status) => {
      if (status === ELoadingStatus.idle) {
        store.dispatch(loadCurrentUser());
        // Wait for the first loaded/error after we triggered the load
        return store.select(selectorCurrentUserStatus).pipe(
          filter((s) => s === ELoadingStatus.loaded || s === ELoadingStatus.error),
          take(1),
        );
      }

      if (status === ELoadingStatus.loaded) {
        return of(status);
      }

      if (status === ELoadingStatus.error) {
        store.dispatch(loadCurrentUser());
        return store.select(selectorCurrentUserStatus).pipe(
          filter((s) => s === ELoadingStatus.loaded || s === ELoadingStatus.error),
          take(1),
        );
      }

      // Status is loading from somewhere else; wait for it to resolve
      return store.select(selectorCurrentUserStatus).pipe(
        filter((s) => s === ELoadingStatus.loaded || s === ELoadingStatus.error),
        take(1),
      );
    }),
    switchMap(() =>
      combineLatest([store.select(selectorCurrentUser), store.select(selectorBusinessDetail)]).pipe(take(1)),
    ),
    map(([user, businessDetail]) => {
      const kycStatus = resolveRoutedKycStatus(user, businessId, businessDetail);
      const isKycActive = kycStatus === KYCStatus.ACTIVE;
      const isKycInReview = kycStatus === KYCStatus.IN_REVIEW;

      // Submitted KYB: keep users on the review page instead of the locked dashboard
      if (isKycInReview && businessId && isDashboardRoute) {
        return router.createUrlTree(["/", businessId, "verify-business"]);
      }

      // Non-KYC users: only allow dashboard, verify-business, profile; block everything else
      if (!isKycActive && businessId && !isDashboardRoute && !isVerifyBusinessRoute && !isProfileRoute) {
        const fallbackRoute = isKycInReview ? "verify-business" : "dashboard";
        return router.createUrlTree(["/", businessId, fallbackRoute]);
      }

      // KYC-active users: block wallets from non-account-manager role is handled by roleGuard, allow all
      return true;
    }),
  );
};
